// Synthesized koto-style ambient: random pentatonic plucks over a low pad,
// with a feedback delay for room and a low-pass to soften harshness.
// All gains are conservative so 0..1 volume from the player maps to a
// pleasant range without clipping.

export interface AmbientHandle {
  setVolume(v: number): void;
  stop(): void;
}

// Hirajoshi-flavoured scale rooted at A (two octaves) — pleasant, vaguely
// Japanese, no dissonant intervals so random order always sounds fine.
const SCALE_HZ = [
  220.00, 261.63, 277.18, 329.63, 392.00,
  440.00, 523.25, 554.37, 659.25, 783.99,
];

export function startKotoAmbient(ctx: AudioContext, destination: AudioNode): AmbientHandle {
  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(destination);

  const tone = ctx.createBiquadFilter();
  tone.type = 'lowpass';
  tone.frequency.value = 2200;
  tone.Q.value = 0.6;
  tone.connect(master);

  // Feedback delay → "room" tail without a convolver.
  const delay = ctx.createDelay(2.0);
  delay.delayTime.value = 0.42;
  const feedback = ctx.createGain();
  feedback.gain.value = 0.34;
  const wet = ctx.createGain();
  wet.gain.value = 0.45;
  delay.connect(feedback).connect(delay);
  delay.connect(wet).connect(tone);

  // Sub pad — two detuned sines for warmth.
  const pad1 = ctx.createOscillator();
  const pad2 = ctx.createOscillator();
  pad1.type = 'sine';
  pad2.type = 'sine';
  pad1.frequency.value = 110.0;
  pad2.frequency.value = 110.4;
  const padGain = ctx.createGain();
  padGain.gain.value = 0.085;
  pad1.connect(padGain);
  pad2.connect(padGain);
  padGain.connect(tone);
  pad1.start();
  pad2.start();

  let stopped = false;
  let timeoutId: number | null = null;

  const pluck = () => {
    if (stopped) return;
    const now = ctx.currentTime;
    const f = SCALE_HZ[Math.floor(Math.random() * SCALE_HZ.length)];

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = f;

    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = f * 1.6;
    bp.Q.value = 3.5;

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.38, now + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 2.4);

    osc.connect(bp).connect(g);
    g.connect(tone);
    g.connect(delay);

    osc.start(now);
    osc.stop(now + 2.6);

    const nextMs = 1700 + Math.random() * 2300;
    timeoutId = window.setTimeout(pluck, nextMs);
  };

  timeoutId = window.setTimeout(pluck, 200 + Math.random() * 600);

  return {
    setVolume(v: number) {
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setTargetAtTime(v, t, 0.12);
    },
    stop() {
      stopped = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setTargetAtTime(0, t, 0.12);
      window.setTimeout(() => {
        try { pad1.stop(); } catch { /* ignore */ }
        try { pad2.stop(); } catch { /* ignore */ }
        try { master.disconnect(); } catch { /* ignore */ }
      }, 800);
    },
  };
}
